'use client';

import { useMemo, type ReactNode } from 'react';
import { NextIntlClientProvider, useLocale, useMessages, type AbstractIntlMessages } from 'next-intl';

/** next-intl 不自动合并消息；只复制新增的对象分支，数组和 ICU 字符串保持原值。 */
export function mergeMessages(inherited: Record<string, unknown>, additional: Record<string, unknown>): Record<string, unknown> {
    const merged = { ...inherited };
    for (const [key, value] of Object.entries(additional)) {
        const previous = inherited[key];
        merged[key] = previous && value && typeof previous === 'object' && typeof value === 'object'
            && !Array.isArray(previous) && !Array.isArray(value)
            ? mergeMessages(previous as Record<string, unknown>, value as Record<string, unknown>)
            : value;
    }
    return merged;
}

/** 独立公开 client 入口，不由根 Provider 重导出；继承 locale 与共享 i18n，不创建第二个语言运行时。 */
export function AdditionalMessagesProvider({ messages, children }: {
    messages: Record<string, unknown>;
    children: ReactNode;
}) {
    const locale = useLocale();
    const inherited = useMessages();
    const combined = useMemo(() => mergeMessages(inherited, messages) as AbstractIntlMessages, [inherited, messages]);
    return <NextIntlClientProvider locale={locale} messages={combined}>{children}</NextIntlClientProvider>;
}
